import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { configApi } from '../../configApi';
import { ITitleSubmission } from './types';

interface IListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: { id: string }[];
}

const fetchSubmissionList = async (registrationId: string, stepId: string): Promise<IListResponse> => {
  const response = await configApi.get<IListResponse>('/api/title-submissions/', {
    params: { registration: registrationId, step: stepId },
  });
  return response.data;
};

const createSubmission = async (registrationId: string, stepId: string): Promise<{ id: string }> => {
  const response = await configApi.post<{ id: string }>('/api/title-submissions/', {
    registration: registrationId,
    step: stepId,
  });
  return response.data;
};

const fetchSubmissionDetail = async (submissionId: string): Promise<ITitleSubmission> => {
  const response = await configApi.get<ITitleSubmission>(`/api/title-submissions/${submissionId}/`);
  return response.data;
};

export const useGetOrCreateTitleSubmission = (registrationId: string, stepId: string) => {
  const queryClient = useQueryClient();
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<unknown>(null);

  const listQuery = useQuery({
    queryKey: ['title-submission-check', registrationId, stepId],
    queryFn: () => fetchSubmissionList(registrationId, stepId),
    enabled: !!registrationId && !!stepId,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: () => createSubmission(registrationId, stepId),
    onSuccess: (data) => {
      setSubmissionId(data.id);
      queryClient.invalidateQueries({ queryKey: ['title-submission', data.id] });
    },
    onError: (err) => {
      setCreateError(err);
    },
  });

  useEffect(() => {
    if (!listQuery.data) return;
    if (listQuery.data.results.length > 0) {
      setSubmissionId(listQuery.data.results[0].id);
    } else if (!createMutation.isPending && !createMutation.isSuccess && !createError) {
      createMutation.mutate();
    }
  }, [listQuery.data]);

  const detailQuery = useQuery({
    queryKey: ['title-submission', submissionId],
    queryFn: () => fetchSubmissionDetail(submissionId!),
    enabled: !!submissionId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const isLoading =
    listQuery.isLoading || createMutation.isPending || (!!submissionId && detailQuery.isLoading);

  const error = listQuery.error || createError || detailQuery.error;

  return {
    submission: detailQuery.data ?? null,
    isLoading,
    error,
    refetch: () => detailQuery.refetch(),
  };
};
